import { DatePicker } from "antd";
import moment from "moment";
import {
  Chart,
  ChartWrapperOptions,
  GoogleChartWrapper,
} from "react-google-charts";
import { useRef, useState, useEffect } from "react";

import styles from "./Charts.module.scss";

import { useCompanyOfficeRequests, useITDepartmentRequests } from "../../hooks";
import { DateFormat, requestStatus } from "../../../../../constants";
import { calculateStatistic } from "../../services";

const { RangePicker } = DatePicker;

export const options: ChartWrapperOptions["options"] = {
  chartArea: { width: "90%", height: "90%" },
  isStacked: true,
  legend: { position: "none" },
  colors: ["#06b6d4", "#9ca3af", "#f87171"],
};

export const pieChartOptions: ChartWrapperOptions["options"] = {
  title: "Tỷ lệ số phiếu của các phòng ban trong năm 2023",
  chartArea: { width: "90%" },
  colors: [
    "#a5b4fc",
    "#fca5a5",
    "#fde68a",
    "#a5f3fc",
    "#c4b5fd",
    "#93c5fd",
    "#6ee7b7",
  ],
};

const fieldsWantToTake = [
  [requestStatus.completed],
  [requestStatus.submitted, requestStatus.preApproved],
  [requestStatus.rejected],
];
 
export const Charts = () => {
  const [rangeDate, setRangeDate] = useState<
    [moment.Moment, moment.Moment] | null
  >(null);
  const columnChartRef = useRef<GoogleChartWrapper | null>(null);

  const { allITDepartmentRequests, isLoading: isITDepartmentLoading } =
    useITDepartmentRequests();
  const { allCompanyOfficeRequests, isLoading: isCompanyOfficeLoading } =
    useCompanyOfficeRequests();

  const isLoading = isITDepartmentLoading || isCompanyOfficeLoading;

  const { column: columnChartValues, pie: pieChartValues } = calculateStatistic(
    {
      charts: ["column", "pie"],
      items: [
        {
          title: "CNTT",
          requests: allITDepartmentRequests,
          fieldsWantToTake,
        },
        {
          title: "VPCT",
          requests: allCompanyOfficeRequests,
          fieldsWantToTake,
        },
      ],
      filters: [
        {
          field: "filter by rangeDate", // ignore this field
          value: rangeDate,
        },
      ],
    }
  );

  const columnChartData = [
    [
      "Department",
      "Đã duyệt",
      { role: "annotation" },
      "Đang đợi duyệt",
      { role: "annotation" },
      "Đã từ chối",
      { role: "annotation" },
    ],
    ...columnChartValues,
    ["KHĐT", 0, "0%", 0, "0%", 0, "0%"],
    ["TCNL", 0, "0%", 0, "0%", 0, "0%"],
    ["TTĐT", 0, "0%", 0, "0%", 0, "0%"],
    ["TTKST", 0, "0%", 0, "0%", 0, "0%"],
    ["TCKT", 0, "0%", 0, "0%", 0, "0%"],
  ];

  const pieChartData = [
    ["Department", "Percent"],
    ["TTĐT", 0],
    ...pieChartValues,
    ["TCKT", 0],
    ["TTKST ", 0],
    ["KHĐTDT ", 0],
    ["TCNL ", 0],
  ];

  useEffect(() => {
    const handleResize = () => {
      if (columnChartRef.current) {
        columnChartRef.current.draw();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className={styles.container}>
      <div>
        <RangePicker
          value={rangeDate}
          placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
          format={DateFormat.DD_MM_YYY}
          onChange={(dates: any) => setRangeDate(dates)}
        />
      </div>

      {isLoading ? (
        <div className={styles.loading}>Đang tính toán dữ liệu...</div>
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.columnChart}>
            <Chart
              chartType="ColumnChart"
              height="500px"
              width="100%"
              data={columnChartData}
              options={options}
              getChartWrapper={(chartWrapper) => {
                columnChartRef.current = chartWrapper;
              }}
            />
          </div>
          <div className={styles.pieChart}>
            <Chart
              chartType="PieChart"
              height="500px"
              width="100%"
              data={pieChartData}
              options={pieChartOptions}
            />
          </div>
        </div>
      )}
    </div>
  );
};
